import React, { useRef, useState } from 'react';
import * as yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, FormLabel, Typography } from '@mui/material';
import {
  UnSavedChangesDialog,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import { IconLibrary } from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import AlternateTimes from './AlternateTimes';
import AlternateTimesValidation from './AlternateTimesValidation';

const FORM_ID = 'DISPATCH_ALTERNATE_TIME_FORM';

const schema = [
  {
    control: 'custom',
    customComponent: 'AlternateTimes',
    name: 'alternateTimes',
    label: {
      localeId: 'SELECT_NEW_TIME_AND_SLOT_S',
      defaultMessage: 'Select new time and slot(s)',
    },
    column: 1,
    row: 1,
    required: true,
    max: 3,
  },
];

function DispatchJobAlternateTimeDialog({
  open,
  returnToJobPage,
  onClose,
  jobInfo,
}) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const [success, setSuccess] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  const jobId = jobInfo?.id;
  const jobNumber = jobInfo?.serviceJobNumber;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl } = useDataProvider(provider);

  const dispatchJobWithAlternateTime = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/dispatch`;
    const response = await post(url, {
      ...data,
      recordVersion: jobInfo.recordVersion,
    });
    return response;
  };

  const onDialogClose = () => {
    if (success) {
      returnToJobPage(null);
    } else if (formRef.current.isDirty()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose(null);
    }
  };

  const buttons = [
    {
      label: formatMessage({
        id: 'DISPATCH',
        defaultMessage: 'Dispatch',
      }),
      onClick: () => formRef.current.submit({ formId: FORM_ID }),
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Dispatch />,
    },
    {
      label: formatMessage({
        id: 'CANCEL',
        defaultMessage: 'Cancel',
      }),
      onClick: () => onDialogClose(),
      variant: 'outlined',
      color: 'primary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const validationSchema = formatMessageFn =>
    yup
      .object({
        alternateTimes: AlternateTimesValidation(formatMessageFn),
      })
      .required();

  const onSubmit = async data => {
    const response = await dispatchJobWithAlternateTime(data);
    if (response?.ok) {
      return {
        nextValues: data,
        errors: null,
        callback: () => {
          setSuccess(true);
        },
      };
    }

    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onDialogClose(null);
      }}
      maxWidth="md"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.DispatchAlternateTime />
        <FormattedMessage id="DISPATCH_JOB" defaultMessage="Dispatch Job" />
        <FormLabel>
          (
          <FormattedMessage
            id="WITH_ALTERNATE_TIME"
            defaultMessage="With Alternate Time"
          />
          )
        </FormLabel>
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobNumber}
        </Typography>
        <DialogCloseButton
          onClose={() => {
            onDialogClose(null);
          }}
        />
      </DialogTitle>
      <DialogContent>
        {!success ? (
          <Box sx={{ pt: 1 }}>
            <Form
              dialog
              submitWithReadOnlyFields
              formId={FORM_ID}
              ref={formRef}
              onSubmit={onSubmit}
              schema={schema}
              validationSchema={validationSchema(formatMessage)}
              permittedByDefault
              customComponents={{
                AlternateTimes,
              }}
              defaultValues={{
                alternateTimes: [],
              }}>
              <Box
                display="flex"
                sx={{
                  justifyContent: 'center',
                  gap: 2,
                  mt: 3,
                }}>
                {buttons.map(btn => {
                  const { label, ...rest } = btn;
                  return (
                    <Button key={label} {...rest}>
                      {label}
                    </Button>
                  );
                })}
              </Box>
            </Form>
            <UnSavedChangesDialog
              open={showUnsavedChangesDialog}
              onLeave={() => onClose()}
              onStay={() => setShowUnsavedChangesDialog(false)}
            />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }} textAlign="center">
            <Typography>
              <FormattedMessage
                id="JOB_DISPATCHED_SUCCESSFULLY"
                defaultMessage="Job Dispatched Successfully"
              />
            </Typography>
            <Box
              display="flex"
              sx={{
                justifyContent: 'center',
                gap: 2,
                mt: 3,
              }}>
              <Button
                onClick={() => returnToJobPage()}
                variant="outlined"
                startIcon={<IconLibrary.Close />}>
                <FormattedMessage id="CLOSE" defaultMessage="Close" />
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DispatchJobAlternateTimeDialog;
